import React, { useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import PropTypes from 'prop-types'

import { useDialog } from '@galo-monitor/dialog'
import { reloadSchema } from '@galo-monitor/forms'
import resetPasswordSchema from '@galo-monitor/forms/schemas/auth/resetPassword.schema'
import I18n, { useT } from '@galo-monitor/i18n'
import { AuthenticationActions } from '@galo-monitor/stores/authentication'
import Button from '@galo-monitor/web-components/Button'
import Form from '@galo-monitor/web-components/Form'
import InputPassword from '@galo-monitor/web-components/InputPassword'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import LoadingModal from '@galo-monitor/web-containers/modals/LoadingModal'
import SuccessModal from '@galo-monitor/web-containers/modals/SuccessModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/guest'

import { Container, Title } from './styles'

const ResetPasswordForm = forwardRef((props, formRef) => {
  const { withoutSubmitButton, loading } = props

  const dispatch = useCallback(useDispatch(), [])
  const { createDialog, removeDialog } = useDialog()
  const t = useT()
  const history = useHistory()
  const { search } = useLocation()
  const { routes } = useRoutes()

  const token = search.split('=')[1]

  const validateSamePassword = useCallback(
    () => reloadSchema(formRef, true),
    [formRef]
  )

  const handleSubmit = useCallback(
    (data) => {
      createDialog({
        id: 'loading',
        Component: LoadingModal
      })

      const removeLoader = () => {
        setTimeout(() => removeDialog({ id: 'loading' }), 150)
      }

      dispatch(AuthenticationActions.resetPassword(
        {
          ...data,
          token
        },
        () => {
          removeLoader()
          createDialog({
            id: 'success',
            Component: SuccessModal,
            props: {
              title: t('password registered with success'),
              message: t('password changed success message')
            }
          })
          history.replace(routes.login.path)
        },
        err => {
          const { status } = err.response

          const errorProps = status === 422 ? {
            title: t('expired token error title'),
            message: t('expired token error message')
          } : {
            title: t('unexpected error occurred'),
            message: t('try again contact support')
          }

          removeLoader()
          createDialog({
            id: 'error',
            Component: ErrorModal,
            props: errorProps
          })
          history.push('/forgot-password')
        }
      ))
    },
    [createDialog, dispatch, history, removeDialog, routes.login.path, t, token]
  )

  const submitOnEnter = useCallback(({ key }) => key === 'Enter' && formRef.current.submit(), [formRef])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ resetPasswordSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Title>
          <I18n>new password register</I18n>
        </Title>

        <InputPassword
          name="password"
          label={ t('new password') }
          disabled={ loading }
          onChange={ validateSamePassword }
          onKeyUp={ submitOnEnter }
          fullWidth
        />

        <InputPassword
          name="password_confirmation"
          label={ t('repeat new password') }
          disabled={ loading }
          onChange={ validateSamePassword }
          onKeyUp={ submitOnEnter }
          fullWidth
        />

        {!withoutSubmitButton && (
          <Button
            id="reset-password-submit"
            onClick={ () => formRef.current.submit() }
            disabled={ loading }
            fullWidth
          >
            <I18n>register new password</I18n>
          </Button>
        )}
      </Container>
    </Form>
  )
})

ResetPasswordForm.propTypes = {
  loading: PropTypes.bool,
  withoutSubmitButton: PropTypes.bool
}

ResetPasswordForm.defaultProps = {
  loading: false,
  withoutSubmitButton: false
}

export default ResetPasswordForm
